import { Tab, useCreatePath, useGetOne, useRecordContext } from "react-admin";
import { useCallback, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, IconButton, Link, Tooltip } from "@mui/material";
import { ChevronRight, DescriptionOutlined } from "@mui/icons-material";
import { PageHeader } from "@/components/common/PageHeader";
import { SideDrawer } from "@/components/common/SideDrawer";
import { AuditLogBox } from "@/components/common/AuditLogBox";
import { DocumentsPanel, AttachmentPreview, usePinnedDocument } from "@/components/common/DocumentsPanel";
import { PolicyPdfUpload } from "./components/PolicyPdfUpload";
import { StyledShow } from "@/components/ui/react-admin/StyledShow";
import { PageHeaderActions } from "@/components/common/PageHeaderActions";
import { RecordCommentsButton } from "@/components/common/comments/RecordCommentsButton";
import { StyledTabbedShowLayout } from "@/components/ui/react-admin/StyledTabbedShowLayout";
import { PageContent, TabContent } from "@/components/common/PageLayout";
import { getResourceIcon } from "@/utils/icons";
import { PolicyOverviewTab } from "./components/PolicyOverviewTab";
import { PolicyEndorsementsTab } from "./components/PolicyEndorsementsTab";
import { LaseredClaimantsTab } from "./components/LaseredClaimantsTab";
import { EndorsementDetailPanel } from "./components/EndorsementDetailPanel";
import { EndorsementCreatePanel } from "./endorsements/EndorsementCreatePanel";
import { EndorsementEditPanel } from "./endorsements/EndorsementEditPanel";
import { ResourceName, type Client, type DocumentRead, type Policy, type Vendor } from "@/types";
import { PolicyStatus } from "@/types/policy";
import { TerminationDateDialog } from "./components/TerminationDateDialog";
import { useResourcePermissions } from "@/hooks/useResourcePermissions";
import { useEndorsements } from "./hooks/useEndorsements";
import { usePolicyHasAttachments } from "./hooks/usePolicyHasAttachments";
import { composeDocumentLabel } from "@/utils/formatters/documentLabel";

// ─────────────────────────────────────────────────────────────
// Drawer content discriminated union
// ─────────────────────────────────────────────────────────────

export type DrawerContent =
  | { type: "endorsement"; endorsementId: string }
  | { type: "endorsement-create" }
  | { type: "endorsement-edit"; endorsementId: string }
  | null;

// ─────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────

const ENDORSEMENT_DRAWER_WIDTH = 520;
const PREVIEW_DRAWER_WIDTH = 600;

/**
 * Thin wrapper — renders StyledShow (which provides RecordContext)
 * then delegates to PolicyShowContent where hooks can access the record.
 */
export const PolicyShow = () => (
  <StyledShow actions={false}>
    <PolicyShowContent />
  </StyledShow>
);

// ─────────────────────────────────────────────────────────────
// Inner content (rendered inside Show → RecordContext.Provider)
// ─────────────────────────────────────────────────────────────

const PolicyShowContent = () => {
  // Preview state is the document **id** only — the drawer title is derived from
  // the document at render (see below), so no filename lives in component state
  // or, via the pin, in localStorage (PHI — ADR-086 / review !4526).
  const [previewDocId, setPreviewDocId] = useState<string | null>(null);
  const [drawerContent, setDrawerContent] = useState<DrawerContent>(null);
  const navigate = useNavigate();
  const createPath = useCreatePath();

  const endorsementDrawerOpen = drawerContent !== null;
  const previewOpen = previewDocId !== null;

  const activeEndorsementId = useMemo(() => {
    if (drawerContent?.type === "endorsement" || drawerContent?.type === "endorsement-edit") {
      return drawerContent.endorsementId;
    }
    return null;
  }, [drawerContent]);

  const closeEndorsementDrawer = () => setDrawerContent(null);

  const policyRecord = useRecordContext<Policy>();
  const policyId = policyRecord?.id as string;
  const clientIdRef = policyRecord?.client_id_ref;
  const vendorIdRef = policyRecord?.vendor_id_ref;

  // The pinned (default) document id — what the top-actions quick-open opens.
  // Persisted (id only) per policy in localStorage so it survives reloads.
  const { pinnedId, setPinnedId } = usePinnedDocument(policyId ? `policy:${policyId}` : undefined);

  const openDocumentPreview = (document: DocumentRead | null) => setPreviewDocId(document ? String(document.id) : null);

  const pinDocument = (document: DocumentRead) => setPinnedId(String(document.id));

  // Navigate the main view to the Documents tab (from the preview drawer) so the
  // user can get back to the full list without feeling lost.
  const goToDocumentsTab = () =>
    void navigate(`${createPath({ resource: ResourceName.POLICIES, id: policyId, type: "show" })}/documents`);

  // "View Policy PDF" from the endorsement detail opens the policy's primary
  // provenance document (its source PDF) in the same preview drawer. Retires
  // the old always-mounted PolicyPDFView while keeping the side-by-side.
  const primarySourceDocId = policyRecord?.source_documents?.[0] ? String(policyRecord.source_documents[0]) : undefined;

  // Quick-open target id: the pinned document, else the policy's primary source
  // document as a sensible default when nothing is pinned yet.
  const quickOpenTarget: string | undefined = pinnedId ?? primarySourceDocId;
  // Top-actions quick-open: pop the drawer to the pinned/default document, or —
  // when the record has nothing to preview yet — take the user to the Documents
  // tab (where they can add one). Always available, never a dead end.
  const openDocumentsQuickAction = () => {
    if (quickOpenTarget) setPreviewDocId(quickOpenTarget);
    else goToDocumentsTab();
  };
  const policyPdfOpen = !!primarySourceDocId && previewDocId === primarySourceDocId;
  const togglePolicyPdf = primarySourceDocId
    ? () => setPreviewDocId((prev) => (prev === primarySourceDocId ? null : primarySourceDocId))
    : undefined;

  // Drawer header title, derived from the previewed document — never a hardcoded
  // "Policy Document" (the string #6507 retires) and never stored. Shares the
  // react-query key with AttachmentPreview's useGetOne, so no extra fetch.
  const { data: previewDoc } = useGetOne<DocumentRead>(
    ResourceName.DOCUMENTS,
    { id: previewDocId ?? "" },
    { enabled: !!previewDocId }
  );
  const previewTitle = previewDoc ? composeDocumentLabel(previewDoc) : "Document";

  const { data: client } = useGetOne<Client>(
    ResourceName.CLIENTS,
    { id: clientIdRef ?? "" },
    { enabled: !!clientIdRef }
  );

  const { data: carrier } = useGetOne<Vendor>(
    ResourceName.VENDORS,
    { id: vendorIdRef ?? "" },
    { enabled: !!vendorIdRef }
  );

  const {
    getById,
    toggleActive,
    updateCodes,
    updateQualifyingConditions,
    updateAdminChangeEntries,
    updateAmendmentEntries,
    updateDomesticReimbursementEntries,
    updateTiers,
    updateStringArray,
    deleteEndorsement,
  } = useEndorsements(policyId, policyRecord?.status === "active");

  // #7320 / ADR-081 D5: changing status to "cancelled" from the header chip needs a
  // termination date in the same update (the API couples the two). The dialog
  // resolves the header's prepareStatusChange promise; dismissing it aborts the
  // status change. Leaving "cancelled" sends no date — the data provider strips it.
  const [terminationDialogOpen, setTerminationDialogOpen] = useState(false);
  const terminationResolver = useRef<((fields: Record<string, unknown> | null) => void) | null>(null);
  const prepareStatusChange = useCallback((newStatus: string): Promise<Record<string, unknown> | null> => {
    if (newStatus !== PolicyStatus.CANCELLED) {
      return Promise.resolve({ termination_date: null });
    }
    return new Promise((resolve) => {
      terminationResolver.current = resolve;
      setTerminationDialogOpen(true);
    });
  }, []);
  const settleTermination = (fields: Record<string, unknown> | null) => {
    setTerminationDialogOpen(false);
    terminationResolver.current?.(fields);
    terminationResolver.current = null;
  };

  const endorsementDrawerTitle = useMemo(() => {
    if (drawerContent?.type === "endorsement-create") return "Add Endorsement";
    if (drawerContent?.type === "endorsement-edit") return "Edit Endorsement";
    if (drawerContent?.type === "endorsement") {
      const endorsement = getById(drawerContent.endorsementId);
      return endorsement?.label ?? "Endorsement";
    }
    return "Endorsement";
  }, [drawerContent, getById]);

  // Total margin-right: account for both drawers when open
  // Use px string to bypass MUI sx spacing multiplier (number × 8px)
  const contentMarginRight =
    (endorsementDrawerOpen ? ENDORSEMENT_DRAWER_WIDTH : 0) + (previewOpen ? PREVIEW_DRAWER_WIDTH : 0);

  const PolicyIcon = getResourceIcon(ResourceName.POLICIES);

  // Title: carrier name (or "Policy" as fallback)
  const policyTitle = carrier?.company_name || "Policy";

  // Year chip next to the title
  const policyYearTag = useMemo(() => {
    const startYear = policyRecord?.effective_start_date?.slice(0, 4);
    const endYear = policyRecord?.effective_end_date?.slice(0, 4);
    if (startYear && endYear) return startYear === endYear ? startYear : `${startYear}–${endYear}`;
    return undefined;
  }, [policyRecord]);

  // Subtitle: client name
  const policySubtitle = client?.client_legal_name || policyRecord?.source_data?.policyholder;

  return (
    <>
      <Box
        sx={{
          transition: "margin-right 0.3s ease",
          marginRight: `${contentMarginRight}px`,
        }}
      >
        {terminationDialogOpen && (
          <TerminationDateDialog
            open
            effectiveStartDate={policyRecord?.effective_start_date}
            effectiveEndDate={policyRecord?.effective_end_date}
            initialValue={policyRecord?.termination_date}
            onConfirm={(terminationDate) => settleTermination({ termination_date: terminationDate })}
            onCancel={() => settleTermination(null)}
          />
        )}
        <PageHeader
          customTitle={policyTitle}
          customSubtitle={policySubtitle}
          customTitleTag={policyYearTag}
          statusKey="status"
          prepareStatusChange={prepareStatusChange}
          rightContent={<PolicyShowActions onOpenDocuments={openDocumentsQuickAction} />}
          headerIcon={<PolicyIcon sx={{ fontSize: 28, color: "primary.main" }} />}
        />
        <PageContent>
          <StyledTabbedShowLayout>
            <Tab label="Overview">
              <PolicyOverviewTab drawerExpanded={previewOpen || endorsementDrawerOpen} />
            </Tab>
            <Tab label="Endorsements" path="endorsements">
              <PolicyEndorsementsTab
                drawerExpanded={endorsementDrawerOpen}
                setDrawerContent={setDrawerContent}
                activeEndorsementId={activeEndorsementId}
              />
            </Tab>
            <Tab label={<DocumentsTabLabel policyId={policyId} />} path="documents">
              <TabContent>
                <DocumentsPanel
                  recordType="policy"
                  recordId={policyId}
                  sourceDocumentIds={policyRecord?.source_documents}
                  selectedDocumentId={previewDocId}
                  onSelectDocument={openDocumentPreview}
                  pinnedDocumentId={pinnedId}
                  onPin={pinDocument}
                  onClearPin={() => setPinnedId(null)}
                  renderUploader={(onUploadComplete) => (
                    <PolicyPdfUpload
                      vendorIdRef={vendorIdRef as string | undefined}
                      clientIdRef={clientIdRef as string | undefined}
                      onUploadComplete={onUploadComplete}
                    />
                  )}
                />
              </TabContent>
            </Tab>
            <Tab label="Lasered Claimants" path="lasered-claimants">
              <LaseredClaimantsTab />
            </Tab>
            <Tab label="Audit Log" path="audit">
              <AuditLogBox tableName={ResourceName.POLICIES} />
            </Tab>
          </StyledTabbedShowLayout>
        </PageContent>
      </Box>

      {/* Endorsement side drawer */}
      <SideDrawer
        title={endorsementDrawerTitle}
        width={ENDORSEMENT_DRAWER_WIDTH}
        open={endorsementDrawerOpen}
        onClose={closeEndorsementDrawer}
        offsetRight={previewOpen ? PREVIEW_DRAWER_WIDTH : 0}
        hideHandle
      >
        {drawerContent?.type === "endorsement" && (
          <EndorsementDetailPanel
            policyId={policyId}
            endorsementId={drawerContent.endorsementId}
            userCanEdit={true} // TODO: wire to useResourcePermissions
            policyIsActive={policyRecord?.status === "active"}
            onEdit={(eid) => setDrawerContent({ type: "endorsement-edit", endorsementId: eid })}
            onToggleActive={(eid) => toggleActive(eid)}
            onUpdateCodes={(eid, codeType, codes) => updateCodes(eid, codeType, codes)}
            onUpdateConditions={(eid, conditions) => updateQualifyingConditions(eid, conditions)}
            onUpdateAdminChangeEntries={(eid, entries) => updateAdminChangeEntries(eid, entries)}
            onUpdateAmendmentEntries={(eid, entries) => updateAmendmentEntries(eid, entries)}
            onUpdateDomesticReimbursementEntries={(eid, entries) => updateDomesticReimbursementEntries(eid, entries)}
            onUpdateTiers={(eid, tiers) => updateTiers(eid, tiers)}
            onUpdateStringArray={(eid, field, values) => updateStringArray(eid, field, values)}
            onDelete={(eid) => deleteEndorsement(eid, closeEndorsementDrawer)}
            pdfOpen={policyPdfOpen}
            onTogglePdf={togglePolicyPdf}
          />
        )}
        {drawerContent?.type === "endorsement-create" && (
          <EndorsementCreatePanel
            policyId={policyId}
            policyIsActive={policyRecord?.status === "active"}
            policyStartDate={policyRecord?.effective_start_date}
            policyEndDate={policyRecord?.effective_end_date}
            onSave={closeEndorsementDrawer}
          />
        )}
        {drawerContent?.type === "endorsement-edit" && (
          <EndorsementEditPanel
            policyId={policyId}
            endorsementId={drawerContent.endorsementId}
            policyIsActive={policyRecord?.status === "active"}
            onSave={() => setDrawerContent({ type: "endorsement", endorsementId: drawerContent.endorsementId })}
            onCancel={() => setDrawerContent({ type: "endorsement", endorsementId: drawerContent.endorsementId })}
          />
        )}
      </SideDrawer>

      {/* Document preview side drawer (single doc, driven by list selection) */}
      <SideDrawer
        title={previewTitle}
        width={PREVIEW_DRAWER_WIDTH}
        open={previewOpen}
        onClose={() => setPreviewDocId(null)}
        hideHandle
      >
        {previewDocId && (
          <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
            <Box sx={{ pb: 1.5 }}>
              <Link
                component="button"
                onClick={goToDocumentsTab}
                underline="hover"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.25,
                  fontSize: "0.8125rem",
                  fontWeight: 500,
                  color: "primary.main",
                  cursor: "pointer",
                }}
              >
                View all documents
                <ChevronRight sx={{ fontSize: 18 }} />
              </Link>
            </Box>
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <AttachmentPreview documentId={previewDocId} />
            </Box>
          </Box>
        )}
      </SideDrawer>
    </>
  );
};

// ─────────────────────────────────────────────────────────────
// Documents tab label — honest attachment count badge (#6507)
// ─────────────────────────────────────────────────────────────

const DocumentsTabLabel = ({ policyId }: { policyId?: string }) => {
  const { count } = usePolicyHasAttachments(policyId);
  return (
    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
      Documents
      {count > 0 && (
        <Box
          component="span"
          sx={{
            fontSize: "0.6875rem",
            fontWeight: 600,
            lineHeight: 1.4,
            px: 0.75,
            borderRadius: 1,
            bgcolor: "action.selected",
            color: "text.secondary",
          }}
        >
          {count}
        </Box>
      )}
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────
// Actions
// ─────────────────────────────────────────────────────────────

const PolicyShowActions = ({ onOpenDocuments }: { onOpenDocuments: () => void }) => {
  const { canDelete } = useResourcePermissions(ResourceName.POLICIES);
  const policyRecord = useRecordContext<Policy>();

  // Documents live in a labelled, always-visible "Documents" tab (ADR-087 D6) —
  // the discoverable entry point. This icon is a secondary quick-open that pops
  // the drawer to the pinned document, or takes the user to the Documents tab
  // when there's nothing to preview yet. Always visible; sits with the other
  // header icons rather than as an out-of-place text button.
  return (
    <PageHeaderActions
      actions={{
        delete: canDelete,
      }}
    >
      <Tooltip title="Documents">
        <IconButton
          size="small"
          aria-label="Open documents"
          onClick={onOpenDocuments}
          sx={{ color: "text.secondary", "&:hover": { color: "primary.main" } }}
        >
          <DescriptionOutlined fontSize="small" />
        </IconButton>
      </Tooltip>
      <RecordCommentsButton
        subjectType={ResourceName.POLICIES}
        subjectId={policyRecord?.id ? String(policyRecord.id) : undefined}
      />
    </PageHeaderActions>
  );
};
